/**
 * mountReactSurface - the one way React roots enter the HUD.
 *
 * Each converted surface gets its own plain div INSIDE an existing
 * hudRegions flex container, as a SIBLING of the imperative surfaces that
 * still mount there - hudRegions.js itself stays imperative, and region
 * stacking keeps working because the div participates in the same flex
 * order model (pass `order` for HUD_ORDER-pinned surfaces).
 *
 * The container div carries no styling beyond pointerEvents (regions are
 * pointerEvents:none; children opt back in) - components own their look via
 * designSystem TOKENS inline styles, same as the imperative UI.
 */
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import type { ReactNode } from 'react';
import { getHudRegion } from '../hud/hudRegions.js';

type HudRegionName = 'top-left' | 'top-right' | 'bottom-right' | 'bottom-center' | 'bottom-left';

export interface MountedSurface {
  container: HTMLDivElement;
  root: Root;
  dispose(): void;
}

function mountInto(
  parent: HTMLElement,
  node: ReactNode,
  opts: { id?: string; order?: number } = {},
): MountedSurface {
  const container = document.createElement('div');
  if (opts.id) container.id = opts.id;
  // A real box (not display:contents): it must be ONE flex item so `order`
  // participates in the region's stacking model like any imperative surface.
  if (opts.order !== undefined) container.style.order = String(opts.order);
  container.style.pointerEvents = 'auto';
  parent.appendChild(container);
  const root = createRoot(container);
  root.render(node);
  return {
    container,
    root,
    dispose() {
      root.unmount();
      container.remove();
    },
  };
}

/** Mount a React surface as a child of a HUD region. */
export function mountReactSurface(
  region: HudRegionName,
  node: ReactNode,
  opts: { id?: string; order?: number } = {},
): MountedSurface {
  return mountInto(getHudRegion(region), node, opts);
}

/**
 * Mount directly under document.body, for fixed-position surfaces that never
 * belonged to a HUD region (the moderation toast, the removed overlay).
 */
export function mountReactBodySurface(node: ReactNode, opts: { id?: string } = {}): MountedSurface {
  return mountInto(document.body, node, opts);
}
